import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  FaExternalLinkAlt,
  FaStar,
  FaBook,
  FaPhone,
  FaPaperclip,
  FaTimes,
  FaPlay,
  FaPause,
  FaPlus,
  FaShareAlt,
  FaFilePdf
} from 'react-icons/fa';
import CustomModal from '../../util_components/custom_modal';
import CustomLoading from '../../util_components/custom_loading';
import CustomPlayer from '../../util_components/custom_video_react';
import Button from '../../util_components/button';
import SmsWindow from '../reports/sms_window';
import SearchableSelect from '../reminders/SearchableSelect';
import ReminderForm from '../reminders/ReminderForm';
import {
  create_task as create_reminder,
  current_team_member,
  prime_team_members,
  list_departments
} from '../reminders/api';
import { openChatWithUserUUID, sendMessageToUserUUID } from '../../chat/chat';
import UserRole from '../../../myt_chat/chat/enums/UserRole';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import { getCookie } from '../../../util_functions';
import { get_api_url } from '../../../utils/urls';
import {
  downloadPersonalisedPlanPdf,
  generatePersonalisedPlanPdfBlob
} from '../../../utils/downloadPersonalisedPlanPdf';
import { get_api, post_api, api_with_method } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';
import rule_config_line from './rule_line';
import { default_agent_filter, filter_query_params, initial_filter_state } from './filters';
import 'video-react/dist/video-react.css';

// The rich outbound-email composer shared with the Support Inbox ("New
// conversation"). Lazy so react-quill / emoji-mart only load when the operator
// actually opens the "Send email" popup.
const NewConversationModal = lazy(() => import('../support_inbox/new_conversation_modal'));

const LIST_PATH = '/sales-task-list';

// Same rich-text toolbar as the early-retention checklist's Review Notes.
const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'clean']
  ]
};
const QUILL_FORMATS = ['bold', 'italic', 'list', 'bullet', 'link'];

// Plain text of rich-text HTML (empty Quill is "<p><br></p>", which strips to '').
const strip_notes_html = (html) => (html || '').replace(/<[^>]*>/g, '').trim();

// True when the Review Notes carry text the agent actually wrote — i.e. more
// than the task tag the form seeds into an empty editor ("<p>#SalesTaskList -
// {task_label}</p>", see the hydrate effect). Completing a task with an
// untouched seed must not send notes_in_hridaya at all, or the backend would
// append a phantom "[Sales Task] {label}" note to the student. Mirrors
// hasManualNote in goserver (PR #803), the server-side half of this defense.
const notes_beyond_seed = (html, task_label) => {
  const text = strip_notes_html(html);
  if (text === '') return false;
  const seed = `#SalesTaskList - ${task_label || ''}`.trim();
  // The bare variant covers a seed created while task_label was still empty.
  return text !== seed && text !== '#SalesTaskList -';
};

/*
 * Query string for /next_task: the task on screen to skip over, plus the filters
 * that scope the walk.
 *
 * Opened from the list (?from=list) it sends the LIST's filters — resolved from
 * the same localStorage blob and the same defaults the list page uses
 * (filters.js) — so "Done and Next" stays inside the set the agent is browsing.
 * Without them the server answers with the globally earliest open task, which is
 * how a concierge filtered to their own list was handed another concierge's task.
 *
 * localStorage is what makes that work now that the list opens the form in a NEW
 * TAB: it is shared across tabs, where router state and redux are not. The one
 * filter it cannot carry is the type-to-search term, which is deliberately not
 * persisted (a search is momentary, not a working preference). The list therefore
 * puts an active search in the link as ?search=… (form_link in index.jsx) and it
 * is read back here, so a walk started from a searched list stays inside the
 * searched set instead of jumping to some other student. Absent param = no search
 * filter, which is exactly the pre-search behaviour.
 *
 * From any other entry point (the student-profile popup, a bookmarked form URL)
 * there is no browsing context, so it falls back to the assignee scope alone —
 * unassigned + self — which is the minimum that keeps that bug from recurring.
 *
 * 'Submitted' is dropped from the status filter: an open task is by definition not
 * submitted, so sending it would guarantee "no more tasks". The server drops it
 * too; doing it here keeps the intent visible on the side that has the context.
 */
const next_task_query = (uuid) => {
  const self_uuid = getAuthUserInfoOrDefault().uuid || '';
  const query = new URLSearchParams(window.location.search);
  const from_list = query.get('from') === 'list';
  const filters = from_list
    ? { ...initial_filter_state(self_uuid), search: query.get('search') || '' }
    : { agent_filter: default_agent_filter(self_uuid) };
  if (filters.status_filter === 'Submitted') filters.status_filter = '';
  const params = filter_query_params(filters);
  params.set('exclude_uuid', uuid);
  return params.toString();
};

// Compact override for the "Message student" action buttons so all four fit on
// one line (the global button rule is roomy). Reduced padding + smaller margin.
const COMPACT_BTN = { padding: '6px 12px', margin: 0, fontSize: 13, whiteSpace: 'nowrap' };

// Timeline dot colours — the ERC map plus the sales-only event kinds.
const TIMELINE_DOT_COLOR = {
  account_created: '#9ca3af',
  membership_signup: '#22c55e',
  plan_change: '#2563eb',
  pause: '#f59e0b',
  trial_booking: '#38bdf8',
  trial_join: '#0d9488',
  trial_reset: '#8b5cf6',
  trial_end: '#f43f5e',
  membership_cancelled: '#ef4444'
};

// ── Membership timeline (ported from the ERC form, extended event kinds) ────
function MembershipTimeline({ events, next_renewal, sessions_remaining }) {
  const has_sessions = typeof sessions_remaining === 'number';
  if ((events || []).length === 0 && !next_renewal && !has_sessions) {
    return (
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
        No membership events.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {has_sessions || next_renewal ? (
        <div className="flex flex-wrap items-center gap-2">
          {has_sessions ? (
            <div
              className="text-sm rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5"
              style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}
            >
              <span style={{ color: 'var(--text-muted)' }}>Sessions remaining:</span>
              <span className="font-semibold" style={{ color: '#1d4ed8' }}>
                {sessions_remaining}
              </span>
            </div>
          ) : null}
          {next_renewal ? (
            <div
              className="text-sm rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5"
              style={{ background: '#ecfdf5', border: '1px solid #a7f3d0' }}
            >
              <span style={{ color: 'var(--text-muted)' }}>Next renewal date:</span>
              <span className="font-semibold" style={{ color: '#047857' }}>
                {next_renewal}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
      {(events || []).map((e, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={idx} className="flex gap-3 items-start">
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: 9999,
              background: TIMELINE_DOT_COLOR[e.type] || '#9ca3af',
              marginTop: 6,
              flexShrink: 0
            }}
          />
          <div className="text-sm">
            <div>
              {/* Three visually distinct pieces: event title, plan name,
                  prepay term. Falls back to `detail` for non-plan events. */}
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                {e.title}
              </span>
              {e.plan_name ? (
                <span className="font-semibold" style={{ color: '#7c3aed' }}>
                  {' — '}
                  {e.plan_name}
                </span>
              ) : null}
              {e.prepay ? (
                <span
                  className="font-semibold rounded px-1.5 py-0.5 ml-1.5"
                  style={{ color: '#9a3412', background: '#ffedd5' }}
                >
                  {e.prepay}
                </span>
              ) : null}
              {!e.plan_name && e.detail ? (
                <span style={{ color: 'var(--text-muted)' }}> — {e.detail}</span>
              ) : null}
            </div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {e.date}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
MembershipTimeline.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object),
  next_renewal: PropTypes.string,
  sessions_remaining: PropTypes.number
};
MembershipTimeline.defaultProps = { events: [], next_renewal: '', sessions_remaining: undefined };

// ── "Sessions" section (ported from the ERC form) ───────────────────────────
// Pill colours for a session's status, bucketed into finished / cancelled-or-
// no-show / upcoming.
const session_status_style = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('finish')) {
    return {
      background: 'var(--state-success-bg, #dcfce7)',
      color: 'var(--state-success-text, #166534)'
    };
  }
  if (s.includes('cancel') || s.includes('no show') || s.includes('replacement')) {
    return {
      background: 'var(--state-error-bg, #fee2e2)',
      color: 'var(--state-error-text, #991b1b)'
    };
  }
  return {
    background: 'var(--state-info-bg, #e0e7ff)',
    color: 'var(--state-info-text, #3730a3)'
  };
};

// Teacher name, hyperlinked to the teacher profile (new tab) when a uuid is
// present. Falls back to plain text / em dash.
const teacher_link = (s) => {
  const name = s.teacher_name || '—';
  if (!s.teacher_uuid) return name;
  return (
    <Link
      to={`/teacherprofile?id=${encodeURIComponent(s.teacher_uuid)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:underline"
    >
      {name}
    </Link>
  );
};

// The card's date/time links to the session details page — PC and GC pages
// take the uuid under different query params.
const session_details_url = (s) =>
  s.kind === 'GC'
    ? `/groupsessiondetails?session_uuid=${encodeURIComponent(s.session_uuid)}`
    : `/sessiondetails?id=${encodeURIComponent(s.session_uuid)}`;

function SessionCard({ s }) {
  const is_gc = s.kind === 'GC';
  const status_style = session_status_style(s.status);
  return (
    <div
      className="rounded-md border p-2"
      // Whole-card tinting separates the session kinds at a glance: PC = blue,
      // GC = purple (matching the kind badge's darker shade of the same hue).
      style={{
        borderColor: is_gc ? '#e9d5ff' : '#bfdbfe',
        background: is_gc ? '#faf5ff' : '#eff6ff'
      }}
    >
      <div className="flex items-center justify-between gap-2">
        {s.session_uuid ? (
          <a
            href={session_details_url(s)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium text-blue-600 hover:underline"
          >
            {s.start_time || '—'}
          </a>
        ) : (
          <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
            {s.start_time || '—'}
          </span>
        )}
        <span
          className="inline-block rounded-full px-2 py-0.5 font-semibold"
          style={{ ...status_style, fontSize: 10 }}
        >
          {s.status || '—'}
        </span>
      </div>
      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
        <span
          className="inline-block rounded px-1.5 py-0.5 font-bold"
          style={{
            fontSize: 10,
            background: is_gc ? '#f3e8ff' : '#dbeafe',
            color: is_gc ? '#6b21a8' : '#1e40af'
          }}
        >
          {s.kind}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
          {is_gc ? (
            <>
              {s.class_name || 'Group class'}
              {s.teacher_name ? <> — {teacher_link(s)}</> : null}
            </>
          ) : (
            teacher_link(s)
          )}
        </span>
        {!is_gc && s.duration ? (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {s.duration} min</span>
        ) : null}
      </div>
      {s.rating ? (
        <div className="mt-1 flex items-start gap-1 text-xs" style={{ color: 'var(--text-muted)' }}>
          <span className="inline-flex items-center" style={{ color: '#f59e0b' }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <FaStar key={n} size={10} color={n <= s.rating ? '#f59e0b' : '#d1d5db'} />
            ))}
          </span>
          {s.review ? <span className="italic">“{s.review}”</span> : null}
        </div>
      ) : null}
    </div>
  );
}
SessionCard.propTypes = { s: PropTypes.object.isRequired };

function SessionList({ title, list, view_all_url, view_all_label }) {
  return (
    <div className="flex-1" style={{ minWidth: 260 }}>
      {view_all_url ? (
        <a
          href={view_all_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block rounded border px-2 py-1 mb-1.5 text-xs font-medium text-blue-600 hover:underline"
          style={{ borderColor: 'var(--border-default)', background: 'var(--surface-card)' }}
        >
          {view_all_label} ↗
        </a>
      ) : null}
      <div
        className="text-xs font-semibold uppercase tracking-wide mb-1.5"
        style={{ color: 'var(--text-primary)' }}
      >
        {title}
      </div>
      {!list || list.length === 0 ? (
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          No sessions.
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {list.map((s, idx) => (
            <SessionCard key={s.session_uuid || idx} s={s} />
          ))}
        </div>
      )}
    </div>
  );
}
SessionList.propTypes = {
  title: PropTypes.string.isRequired,
  list: PropTypes.arrayOf(PropTypes.object),
  view_all_url: PropTypes.string,
  view_all_label: PropTypes.string
};
SessionList.defaultProps = { list: [], view_all_url: '', view_all_label: '' };

// ── "Recent communications" — merged chat / SMS / WhatsApp / call / session
// timeline (newest first). Each kind gets a coloured chip; calls expose a
// transcript popup + an on-demand recording link.
const COMM_CHIP = {
  chat: { label: 'Chat', bg: '#e0e7ff', color: '#3730a3' },
  sms: { label: 'SMS', bg: '#dcfce7', color: '#166534' },
  whatsapp: { label: 'WhatsApp', bg: '#d1fae5', color: '#065f46' },
  call: { label: 'Call', bg: '#fef3c7', color: '#92400e' },
  session: { label: 'Session', bg: '#f3e8ff', color: '#6b21a8' },
  note: { label: 'Note', bg: '#f1f5f9', color: '#475569' }
};

// Sentiment pill for analysed calls — state tokens so it reads in both themes.
const SENTIMENT_PILL = {
  positive: { bg: 'var(--state-success-bg)', color: 'var(--state-success-text)' },
  negative: { bg: 'var(--state-error-bg)', color: 'var(--state-error-text)' },
  neutral: { bg: 'var(--surface-sunken)', color: 'var(--text-muted)' }
};

// mm:ss formatter for the recording timer.
const fmt_secs = (s) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${String(ss).padStart(2, '0')}`;
};

// Inline call-recording player: play/pause, seek slider, timer. The signed URL
// is fetched lazily on first play (it expires, so we don't mint it up front).
function CallRecordingPlayer({ callUuid, fetchRecording, onError }) {
  const audio_ref = useRef(null);
  const want_play_ref = useRef(false);
  const [url, set_url] = useState('');
  const [loading, set_loading] = useState(false);
  const [playing, set_playing] = useState(false);
  const [cur, set_cur] = useState(0);
  const [dur, set_dur] = useState(0);

  const toggle = () => {
    const a = audio_ref.current;
    if (url && a) {
      if (playing) a.pause();
      else a.play();
      return;
    }
    if (loading) return;
    set_loading(true);
    fetchRecording(callUuid)
      .then((u) => {
        set_loading(false);
        if (!u) {
          onError('No recording available for this call');
          return;
        }
        want_play_ref.current = true; // autoplay once the audio can play
        set_url(u);
      })
      .catch(() => {
        set_loading(false);
        onError('Failed to load recording');
      });
  };

  const seek = (e) => {
    const a = audio_ref.current;
    const t = Number(e.target.value);
    if (a) a.currentTime = t;
    set_cur(t);
  };

  return (
    <div className="flex items-center gap-2 mt-1.5" style={{ maxWidth: 360 }}>
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Pause recording' : 'Play recording'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 26,
          width: 26,
          borderRadius: 9999,
          border: '1px solid #2563eb',
          background: '#2563eb',
          color: '#ffffff',
          padding: 0,
          margin: 0,
          cursor: loading ? 'wait' : 'pointer',
          flexShrink: 0
        }}
      >
        {/* eslint-disable-next-line no-nested-ternary */}
        {loading ? '…' : playing ? <FaPause size={11} /> : <FaPlay size={11} />}
      </button>
      <input
        type="range"
        className="stl-range"
        min={0}
        // Positive fallback until metadata loads — with max=0 the thumb renders
        // mid-track with a blue sliver; cur/1 = 0% keeps it hard-left instead.
        max={dur > 0 ? dur : 1}
        step="0.1"
        value={cur}
        onChange={seek}
        // The .stl-range class (common.css) strips the leaked global input
        // border/box-shadow/outline pill across hover/focus states.
        style={{ flex: 1, accentColor: '#2563eb', cursor: 'pointer', margin: 0 }}
      />
      <span className="text-xs tabular-nums" style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
        {fmt_secs(cur)} / {fmt_secs(dur)}
      </span>
      {url ? (
        <audio
          ref={audio_ref}
          src={url}
          preload="metadata"
          style={{ display: 'none' }}
          onLoadedMetadata={(e) => set_dur(e.target.duration || 0)}
          onCanPlay={() => {
            if (want_play_ref.current && audio_ref.current) {
              want_play_ref.current = false;
              audio_ref.current.play();
            }
          }}
          onTimeUpdate={(e) => set_cur(e.target.currentTime || 0)}
          onPlay={() => set_playing(true)}
          onPause={() => set_playing(false)}
          onEnded={() => {
            set_playing(false);
            set_cur(0);
          }}
        />
      ) : null}
    </div>
  );
}
CallRecordingPlayer.propTypes = {
  callUuid: PropTypes.string.isRequired,
  fetchRecording: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired
};

function RecentCommunications({
  events,
  onOpenTranscript,
  fetchRecording,
  onPlaySession,
  onError
}) {
  if (!events || events.length === 0) {
    return (
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
        No recent communications.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2" style={{ maxWidth: 760 }}>
      {events.map((e, idx) => {
        const chip = COMM_CHIP[e.kind] || { label: e.kind, bg: '#e5e7eb', color: '#374151' };
        const inbound = e.direction === 'in';
        return (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            className="rounded-md border px-3 py-2"
            style={{ borderColor: 'var(--border-default)', background: 'var(--surface-card)' }}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="inline-block rounded-full px-2 py-0.5 font-semibold"
                style={{ fontSize: 10, background: chip.bg, color: chip.color }}
              >
                {chip.label}
              </span>
              {e.kind !== 'session' && e.direction ? (
                <span
                  className="inline-block rounded px-1.5 py-0.5 font-semibold"
                  style={{
                    fontSize: 9,
                    background: inbound ? '#eff6ff' : '#f1f5f9',
                    color: inbound ? '#1e40af' : '#475569'
                  }}
                >
                  {inbound ? 'From member' : 'To member'}
                </span>
              ) : null}
              {e.title ? (
                // Session title links to its details page (PC vs GC route);
                // everything else stays plain text.
                e.kind === 'session' && e.session_uuid ? (
                  <a
                    href={
                      e.session_kind === 'GC'
                        ? `/groupsessiondetails?session_uuid=${encodeURIComponent(e.session_uuid)}`
                        : `/sessiondetails?id=${encodeURIComponent(e.session_uuid)}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-600 hover:underline"
                  >
                    {e.title}
                  </a>
                ) : (
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {e.title}
                  </span>
                )
              ) : null}
              {e.session_kind ? (
                <span
                  className="inline-block rounded px-1.5 py-0.5 font-bold"
                  style={{
                    fontSize: 9,
                    background: e.session_kind === 'GC' ? '#f3e8ff' : '#dbeafe',
                    color: e.session_kind === 'GC' ? '#6b21a8' : '#1e40af'
                  }}
                >
                  {e.session_kind}
                </span>
              ) : null}
              {e.status ? (
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {e.status}
                </span>
              ) : null}
              <span className="ml-auto text-xs" style={{ color: 'var(--text-muted)' }}>
                {e.date}
              </span>
            </div>
            {e.teacher_name ? (
              <div className="text-xs mt-1" style={{ color: 'var(--text-secondary, #475569)' }}>
                with{' '}
                {e.teacher_uuid ? (
                  <a
                    href={`/teacherprofile?id=${encodeURIComponent(e.teacher_uuid)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {e.teacher_name}
                  </a>
                ) : (
                  e.teacher_name
                )}
              </div>
            ) : null}
            {e.kind === 'session' && e.has_recording && e.session_uuid ? (
              <button
                type="button"
                onClick={() => onPlaySession(e.session_uuid)}
                className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:underline"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <FaPlay size={9} /> Play recording
              </button>
            ) : null}
            {e.body ? (
              <div
                className="text-sm mt-1"
                style={{
                  color: 'var(--text-primary)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {e.body}
              </div>
            ) : null}
            {e.kind === 'call' ? (
              <>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  {e.duration ? (
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {Math.round(e.duration / 60)}m {e.duration % 60}s
                    </span>
                  ) : null}
                  {e.sentiment ? (
                    <span
                      className="inline-block rounded-full px-2 py-0.5 font-semibold capitalize"
                      style={{
                        fontSize: 10,
                        background: (
                          SENTIMENT_PILL[e.sentiment.toLowerCase()] || SENTIMENT_PILL.neutral
                        ).bg,
                        color: (SENTIMENT_PILL[e.sentiment.toLowerCase()] || SENTIMENT_PILL.neutral)
                          .color
                      }}
                    >
                      {e.sentiment}
                    </span>
                  ) : null}
                  {e.talk_ratio_customer || e.talk_ratio_agent ? (
                    <span
                      className="inline-block rounded-full px-2 py-0.5 font-medium"
                      style={{
                        fontSize: 10,
                        background: 'var(--surface-sunken)',
                        color: 'var(--text-muted)'
                      }}
                    >
                      customer / agent - {Math.round(e.talk_ratio_customer || 0)} /{' '}
                      {Math.round(e.talk_ratio_agent || 0)}
                    </span>
                  ) : null}
                  {e.transcript ? (
                    <button
                      type="button"
                      onClick={() => onOpenTranscript(e)}
                      className="text-xs font-medium text-blue-600 hover:underline"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    >
                      View transcript
                    </button>
                  ) : null}
                </div>
                {Array.isArray(e.concerns) && e.concerns.length > 0 ? (
                  <ul
                    className="mt-1.5 list-disc pl-5 text-xs"
                    style={{ color: 'var(--text-secondary, #475569)' }}
                  >
                    {e.concerns.map((c, ci) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={ci}>{c}</li>
                    ))}
                  </ul>
                ) : null}
                {e.has_recording && e.call_uuid ? (
                  <CallRecordingPlayer
                    callUuid={e.call_uuid}
                    fetchRecording={fetchRecording}
                    onError={onError}
                  />
                ) : null}
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
RecentCommunications.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object),
  onOpenTranscript: PropTypes.func.isRequired,
  fetchRecording: PropTypes.func.isRequired,
  onPlaySession: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired
};
RecentCommunications.defaultProps = { events: [] };

// Full-width section header bar — same markup as the ERC form's field-section
// headers (sunken background, brand-blue left border, "(auto)" tag for
// server-computed sections).
function SectionHeader({ label, is_auto }) {
  return (
    <div
      className="flex items-center text-sm font-semibold mb-3 px-3 py-1.5 rounded-md"
      style={{
        color: 'var(--text-primary)',
        background: 'var(--surface-sunken, #f1f5f9)',
        borderLeft: '3px solid var(--brand-primary, #2563eb)'
      }}
    >
      {label}
      {is_auto ? (
        <span className="ml-2 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>
          (auto)
        </span>
      ) : null}
    </div>
  );
}
SectionHeader.propTypes = {
  label: PropTypes.string.isRequired,
  is_auto: PropTypes.bool
};
SectionHeader.defaultProps = { is_auto: false };

// One "label: value" attribute cell.
function AttrCell({ label, children }) {
  return (
    <div>
      <div className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
        {label}
      </div>
      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
        {children}
      </div>
    </div>
  );
}
AttrCell.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

// Section 1 of the sales task form: student name (links to profile), the rule
// that generated the task, member attributes from the student table, the
// latest 1-on-1 session, and the inline AI "getting to know student better"
// summary. More sections get added below.
//
// Data comes from /v2/sales_task_list/form/:uuid — auto_data is the
// server-computed payload frozen when the task was generated ({student,
// last_1on1_session, ai_getting_to_know}).
function SalesTaskForm({ uuid }) {
  const [form, set_form] = useState(null);
  const [loading, set_loading] = useState(true);
  const [error, set_error] = useState('');

  useEffect(() => {
    set_loading(true);
    set_error('');
    const url = get_api_url(`/v2/sales_task_list/form/${encodeURIComponent(uuid)}`);
    get_api(url, true)
      .then((res) => {
        set_form(res?.data?.data || null);
        set_loading(false);
      })
      .catch((e) => {
        set_error(e?.response?.data?.reason || 'Failed to load sales task');
        set_loading(false);
      });
  }, [uuid]);

  // Live clock ticking each second so the member's current local time stays
  // current while the form is open.
  const [now, set_now] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => set_now(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Latest-1on1 recording popup (video-react player + chapter strip). The
  // replay (signed URL + transcript + chapters) is fetched lazily on first
  // open — same flow as the early-retention form.
  const [show_session_video, set_show_session_video] = useState(false);
  const [replay, set_replay] = useState(null);
  const [loading_replay, set_loading_replay] = useState(false);
  // Which session's recording the modal is showing — the "Latest 1on1 session
  // done" button and the per-session "Play recording" in Recent communications
  // both drive it, so it's keyed by uuid (re-fetch when it changes).
  const [session_video_uuid, set_session_video_uuid] = useState('');
  const session_player_ref = useRef(null);
  const seek_session_video = (secs) => {
    const player = session_player_ref.current;
    if (!player) return;
    player.seek(secs);
    player.play();
  };

  // "Teacher Created 3 Month Plan" popup — the personalised-plan page embedded
  // in a modal (it reads its student_uuid from the URL, so an iframe keeps the
  // page untouched while avoiding the ERC form's open-in-new-tab behaviour).
  const [show_3m_plan, set_show_3m_plan] = useState(false);
  // "Download PDF" — reuses the shared personalised-plan PDF exporter (renders the
  // student-facing plan page in a hidden iframe and rasterises it via html2canvas +
  // jsPDF). Kept busy-guarded so the button can't be re-triggered mid-export.
  const [downloading_plan_pdf, set_downloading_plan_pdf] = useState(false);
  // "Share" — generates the plan PDF, uploads it to the CDN, then shares the
  // returned URL; busy-guarded like the PDF download (it takes a few seconds).
  const [sharing_plan, set_sharing_plan] = useState(false);

  // "Recent communications" — call transcript popup + on-demand recording. The
  // recording URL is signed server-side per request (never frozen), so play
  // fetches it fresh and opens the audio in a new tab.
  const [call_transcript, set_call_transcript] = useState(null); // {title, date, transcript}

  const open_call_transcript = (e) =>
    set_call_transcript({ title: e.title, date: e.date, transcript: e.transcript });

  // Fetch a fresh signed recording URL for the inline player (lazy, per call).
  const fetch_call_recording = (call_uuid) =>
    get_api(
      get_api_url(`/v2/sales_task_list/call_recording/${encodeURIComponent(call_uuid)}`),
      true
    ).then((res) => res?.data?.data?.recording_url || '');

  // ── "Message teacher" / "Message student" (same behaviour as the ERC form) ─
  const [msg_teacher_uuid, set_msg_teacher_uuid] = useState('');
  // undefined = pristine (shows the default template); a string = user-edited.
  const [msg_teacher_text, set_msg_teacher_text] = useState(undefined);
  const [msg_student_text, set_msg_student_text] = useState('');
  const [sending_teacher, set_sending_teacher] = useState(false);
  const [sending_student, set_sending_student] = useState(false);
  const [show_sms_window, set_show_sms_window] = useState(false);

  // ── Review Notes (rich text + attachments), persisted on task completion ──
  const [review_notes, set_review_notes] = useState('');
  const [attachments, set_attachments] = useState([]); // [{key, name, url}]
  const [uploading, set_uploading] = useState(false);
  const file_input_ref = useRef(null);
  // Seed the notes with a task tag once the form loads (still-open tasks only).
  const notes_seeded_ref = useRef(false);

  // ── footer actions (Done / Skip / Cancel + progress counter) ─────────────
  const history = useHistory();
  const dispatch = useDispatch();
  const notify = (type, message) =>
    dispatch(actions.set_notification_variable(true, type, message));

  const [stats, set_stats] = useState(null); // { remaining, closed_today }
  const [acting, set_acting] = useState(false);

  const load_stats = () => {
    get_api(get_api_url('/v2/sales_task_list/stats'), true)
      .then((res) => set_stats(res?.data?.data || null))
      .catch(() => {
        /* counter is best-effort; the form works without it */
      });
  };
  useEffect(load_stats, [uuid]);

  const go_to_list = () => history.push(LIST_PATH);

  // Navigate to the next open task within the same filtered context (see
  // next_task_query), preserving ?from=list so the back link stays. Falls back to
  // the list when nothing is left.
  const go_to_next = () => {
    const url = get_api_url(`/v2/sales_task_list/next_task?${next_task_query(uuid)}`);
    return get_api(url, true)
      .then((res) => {
        const next = res?.data?.data?.uuid;
        if (!next) {
          notify('success', 'No more pending tasks — all done!');
          go_to_list();
          return;
        }
        // Carry the browsing context forward: ?from keeps the back link (and the
        // list-scoped filters), ?search keeps the walk inside the searched set —
        // dropping it here would make step 2 of a Done-and-Next chain wider than
        // step 1.
        const q = new URLSearchParams(window.location.search);
        const from = q.get('from');
        const search = q.get('search');
        const next_params = new URLSearchParams({ uuid: next });
        if (from) next_params.set('from', from);
        if (search) next_params.set('search', search);
        history.push(`/sales-task-form?${next_params.toString()}`);
      })
      .catch(() => {
        notify('error', 'Failed to find the next task');
      });
  };

  const complete_current = () => {
    const reply = {};
    if (notes_beyond_seed(review_notes, form?.task_label)) reply.notes_in_hridaya = review_notes;
    if (attachments.length) reply.attachments = attachments;
    return post_api(get_api_url('/v2/sales_task_list/complete'), { uuid, reply }, true);
  };

  const done_and_next = () => {
    set_acting(true);
    complete_current()
      .then(() => go_to_next())
      .catch((e) => notify('error', e?.response?.data?.reason || 'Failed to complete task'))
      .finally(() => set_acting(false));
  };

  const skip_and_next = () => {
    set_acting(true);
    go_to_next().finally(() => set_acting(false));
  };

  const done_and_back = () => {
    set_acting(true);
    complete_current()
      .then(() => {
        notify('success', 'Task completed');
        go_to_list();
      })
      .catch((e) => notify('error', e?.response?.data?.reason || 'Failed to complete task'))
      .finally(() => set_acting(false));
  };

  const auto = form?.auto_data || {};
  const { student } = auto;
  const { last_1on1_session } = auto;
  const { ai_getting_to_know } = auto;
  const timeline_events = auto.membership_timeline || [];
  const sessions_overview = auto.sessions_overview || {};
  const recent_communications = auto.recent_communications || [];
  const has_3m_plan = !!auto.has_3m_plan;
  const student_uuid = form?.student_uuid;
  const student_name = form?.student_name;
  const task_label = form?.task_label;

  // Share the 3-month plan: generate the plan PDF client-side (same exporter as
  // Download PDF, as a Blob), upload it to the CDN (/v2/cdn/bunny/upload — the
  // ops role passes student_uuid so the file lands under the student's identity),
  // then hand the returned public CDN URL to the native share sheet, falling back
  // to copy-to-clipboard (with the form's standard toast) when navigator.share is
  // unavailable. A dismissed share sheet (AbortError) is a no-op.
  const share_plan_url = async () => {
    if (!student_uuid || sharing_plan) return;
    set_sharing_plan(true);
    try {
      const { blob, filename } = await generatePersonalisedPlanPdfBlob(student_uuid, student_name);
      const fd = new FormData();
      fd.append('file', blob, filename);
      fd.append('filename', filename);
      fd.append('folder', 'three_month_plans');
      fd.append('student_uuid', student_uuid);
      const res = await api_with_method(
        'post',
        get_api_url('/v2/cdn/bunny/upload'),
        fd,
        true,
        'formdata'
      );
      const url = res?.data?.data?.url;
      if (!url) throw new Error('CDN upload returned no URL');

      const copy_link = async () => {
        await navigator.clipboard.writeText(url);
        notify('success', 'Plan PDF link copied to clipboard');
      };
      try {
        if (navigator.share) {
          await navigator.share({
            url,
            title: `${student_name || 'Student'}'s 3 Month Plan`
          });
        } else {
          await copy_link();
        }
      } catch (e) {
        // User dismissing the native share sheet is not an error.
        if (e?.name === 'AbortError') return;
        await copy_link();
      }
    } catch (e) {
      notify('error', e?.response?.data?.reason || 'Failed to share the 3-month plan');
    } finally {
      set_sharing_plan(false);
    }
  };

  const download_plan_pdf = async () => {
    if (!student_uuid || downloading_plan_pdf) return;
    set_downloading_plan_pdf(true);
    try {
      await downloadPersonalisedPlanPdf(student_uuid, student_name);
    } catch (e) {
      notify('error', 'Failed to download the 3-month plan PDF');
    } finally {
      set_downloading_plan_pdf(false);
    }
  };

  const submitted = form?.status === 'submitted';
  // Auto-closed: the system retired the task because the student became a member,
  // so there is nothing left to chase (goserver's student_became_member lifecycle
  // hook). The row is already gone from every open queue and the Complete
  // endpoint no-ops it, so the form must not keep offering "Done".
  const auto_closed = form?.status === 'auto_closed';
  // Terminal = read-only. Both terminal statuses freeze the form the same way;
  // only the footer message distinguishes them.
  const terminal = submitted || auto_closed;

  // Hydrate Review Notes from the saved reply (or seed with a task tag for a
  // still-open task) once the form loads.
  useEffect(() => {
    if (!form || notes_seeded_ref.current) return;
    notes_seeded_ref.current = true;
    const reply = form.reply || {};
    if (typeof reply.notes_in_hridaya === 'string' && reply.notes_in_hridaya) {
      set_review_notes(reply.notes_in_hridaya);
    } else if (form.status !== 'submitted' && form.status !== 'auto_closed') {
      set_review_notes(`<p>#SalesTaskList - ${form.task_label || ''}</p>`);
    }
    if (Array.isArray(reply.attachments)) set_attachments(reply.attachments);
  }, [form]);

  const upload_attachment = (file) => {
    set_uploading(true);
    const fd = new FormData();
    fd.append('student_uuid', student_uuid);
    fd.append('file', file);
    api_with_method(
      'post',
      get_api_url('/v2/sales_task_list/upload_attachment'),
      fd,
      true,
      'formdata'
    )
      .then((res) => {
        const att = res?.data?.data;
        if (att && att.key) set_attachments((prev) => [...prev, att]);
      })
      .catch((e) => notify('error', e?.response?.data?.reason || 'Failed to upload attachment'))
      .finally(() => set_uploading(false));
  };

  const handle_attachment_change = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) upload_attachment(file);
    e.target.value = '';
  };

  const remove_attachment = (idx) => set_attachments((prev) => prev.filter((_, i) => i !== idx));

  // Append a hashtag to the Review Notes (idempotent) so the note records which
  // channels the agent used — #phone on a call, #email on an email. The
  // #SalesTaskList tag is seeded by default when the form loads.
  const append_note_tag = (tag) => {
    set_review_notes((prev) => {
      const cur = prev || '';
      // Match the whole tag (not a prefix like #phonecall) using a boundary.
      if (new RegExp(`${tag}\\b`, 'i').test(cur.replace(/<[^>]*>/g, ' '))) return cur;
      return `${cur}<p>${tag}</p>`;
    });
  };

  // Messaging context (live fields from the form payload).
  const message_teachers = form?.message_teachers || [];
  const student_slug = form?.student_slug || '';
  const dispatch_method = form?.message_dispatch_method;
  const student_has_phone = !!form?.student_has_phone;
  const can_sms =
    !!student_uuid && student_has_phone && dispatch_method && dispatch_method !== 'none';

  // ── "+ Reminder" floating button (same as the ERC form) ───────────────────
  // Opens the /reminders create form, seeded from this task (linked to the
  // student, high priority, sales/retention department, link back here).
  const [show_reminder, set_show_reminder] = useState(false);
  const [reminder_department_uuid, set_reminder_department_uuid] = useState('');
  useEffect(() => {
    if (!student_uuid) return;
    prime_team_members();
    list_departments().then((rows) => {
      const list = rows || [];
      const dept =
        list.find((d) => (d.name || '').toLowerCase().includes('sales')) ||
        list.find((d) => (d.name || '').toLowerCase().includes('retention'));
      if (dept) set_reminder_department_uuid(dept.uuid);
    });
  }, [student_uuid]);

  const task_link = useMemo(
    () => `${window.location.origin}/sales-task-form?uuid=${encodeURIComponent(uuid)}`,
    [uuid]
  );

  const reminder_initial = useMemo(() => {
    if (!form) return undefined;
    const description = [
      `${task_label || 'Sales'} task follow-up for ${student_name || 'student'}(${student_slug})`,
      '',
      `Generated from the "${task_label || ''}" Sales Task.`,
      `Task link: ${task_link}`
    ].join('\n');
    return {
      description,
      priority: 'High',
      department_uuid: reminder_department_uuid,
      // Start unassigned (an empty array stops ReminderForm defaulting to self).
      assignee_uuids: []
    };
  }, [form, task_label, student_name, student_slug, task_link, reminder_department_uuid]);

  const reminder_locked_user = student_uuid
    ? { uuid: student_uuid, name: student_name, role: 'student' }
    : null;
  const me = current_team_member(getCookie('cms_email'));

  const handle_reminder_submit = (payload) =>
    create_reminder(payload)
      .then(() => {
        set_show_reminder(false);
        notify('success', 'Reminder created');
      })
      .catch((e) => notify('error', e?.response?.data?.reason || 'Failed to create reminder'));

  // Default teacher message (mirrors the ERC template); the edited value wins.
  const default_teacher_message = `Student ${student_name || ''}(${student_slug}) sales follow-up.`;
  const teacher_message_value =
    msg_teacher_text === undefined ? default_teacher_message : msg_teacher_text;

  const send_teacher_message = () => {
    const message = teacher_message_value.trim();
    if (!msg_teacher_uuid) {
      notify('error', 'Select a teacher first');
      return;
    }
    if (!message) {
      notify('error', 'Message is empty');
      return;
    }
    set_sending_teacher(true);
    sendMessageToUserUUID(msg_teacher_uuid, UserRole.TEACHER, message)
      .then((ok) => {
        notify(ok ? 'success' : 'error', ok ? 'Message sent to teacher' : 'Could not send message');
        if (ok) set_msg_teacher_text(undefined); // revert to pristine template
      })
      .catch(() => notify('error', 'Could not send message'))
      .finally(() => set_sending_teacher(false));
  };

  const open_teacher_chat = () => {
    if (!msg_teacher_uuid) {
      notify('error', 'Select a teacher first');
      return;
    }
    openChatWithUserUUID(msg_teacher_uuid, UserRole.TEACHER);
  };

  const send_student_message = () => {
    const message = msg_student_text.trim();
    if (!student_uuid) {
      notify('error', 'No student for this task');
      return;
    }
    if (!message) {
      notify('error', 'Message is empty');
      return;
    }
    set_sending_student(true);
    sendMessageToUserUUID(student_uuid, UserRole.STUDENT, message)
      .then((ok) => {
        notify(ok ? 'success' : 'error', ok ? 'Message sent to student' : 'Could not send message');
        if (ok) set_msg_student_text('');
      })
      .catch(() => notify('error', 'Could not send message'))
      .finally(() => set_sending_student(false));
  };

  const open_student_chat = () => {
    if (!student_uuid) {
      notify('error', 'No student for this task');
      return;
    }
    openChatWithUserUUID(student_uuid, UserRole.STUDENT);
  };

  // Call the student via the app-level Twilio outbound manager — same global
  // events the student profile's "Call User" button uses. Track the global
  // outbound-active flag so the button disables during any in-progress call.
  const [call_active, set_call_active] = useState(!!window.__twilioOutboundActive);
  useEffect(() => {
    const handler = (e) => set_call_active(!!e?.detail?.active);
    window.addEventListener('twilio-outgoing-call-active', handler);
    return () => window.removeEventListener('twilio-outgoing-call-active', handler);
  }, []);
  const call_student = () => {
    if (!student_uuid) {
      notify('error', 'No student for this task');
      return;
    }
    window.dispatchEvent(
      new CustomEvent('twilio-outgoing-call-start', {
        detail: { studentUuid: student_uuid, studentName: student_name || '' }
      })
    );
    append_note_tag('#phone');
  };

  // "Send email" now opens the shared rich composer (the Support Inbox's "New
  // conversation" modal) in a popup, prefilled with the student as recipient. It
  // posts through the support create-conversation API, so the outbound email
  // lands as a proper support thread.
  const student_email = form?.student_email || '';
  const [show_email_composer, set_show_email_composer] = useState(false);
  // Agent/team pickers in the composer come from the support bootstrap; fetch it
  // lazily the first time the composer is opened (empty lists render as
  // placeholders and are optional, so this never blocks sending).
  const support_bootstrap = useSelector((s) => s.support.bootstrap);
  const support_bootstrap_status = useSelector((s) => s.support.bootstrap_status);
  const create_conversation_result = useSelector((s) => s.support.create_conversation_result);
  const composer_agents = useMemo(
    () =>
      (support_bootstrap.agents || []).map((a) => ({
        key: a.uuid,
        label: a.name,
        email: a.email
      })),
    [support_bootstrap.agents]
  );
  const composer_teams = useMemo(
    () =>
      (support_bootstrap.teams || []).map((t) => ({
        key: t.uuid || t.UUID,
        label: t.name || t.Name
      })),
    [support_bootstrap.teams]
  );
  // The Inbox picker on /support lists the team folders (bootstrap teams with a
  // 'team:' key prefix). It's UI-only — the create-conversation contract has no
  // inbox concept — so labels are all that matter; mirror /support's shape.
  const composer_inboxes = useMemo(
    () => composer_teams.map((t) => ({ key: `team:${t.key}`, label: t.label })),
    [composer_teams]
  );
  const open_email_composer = () => {
    if (!student_uuid) {
      notify('error', 'No student for this task');
      return;
    }
    if (!student_email) {
      notify('error', 'Student has no email on file');
      return;
    }
    // Load the support bootstrap (inbox/team/agent lists) if it isn't already
    // in the store — keyed on status (not list length) so a previous failure
    // retries on the next open instead of leaving the pickers empty forever.
    if (support_bootstrap_status !== 'success' && support_bootstrap_status !== 'loading') {
      dispatch(actions.get_support_bootstrap());
    }
    set_show_email_composer(true);
  };

  // ── message playbook (agent-generated) ────────────────────────────────────
  // Insert the generated draft into the chat text box. Appends on
  // a new line when the box already has a draft so nothing typed is lost.
  const add_playbook_to_textbox = (text) => {
    set_msg_student_text((prev) => (prev.trim() ? `${prev.replace(/\s+$/, '')}\n${text}` : text));
  };

  // The agent recommendations are generated each time the popup opens. Goserver owns the
  // prompt construction and calls Antaryami with the sales-task context, so the
  // browser never receives the external API credential.
  const [show_playbook, set_show_playbook] = useState(false);
  const [playbook_draft, set_playbook_draft] = useState(null);
  const [playbook_status, set_playbook_status] = useState('none');
  const load_playbook = () => {
    set_playbook_status('loading');
    set_playbook_draft(null);
    post_api(
      get_api_url('/v2/sales_task_list/message_playbook'),
      { task_uuid: uuid },
      true,
      undefined,
      120000
    )
      .then((res) => {
        set_playbook_draft(res?.data?.data || {});
        set_playbook_status('success');
      })
      .catch(() => {
        set_playbook_status('fail');
        notify('error', 'Could not generate the message playbook');
      });
  };
  const open_playbook = () => {
    set_show_playbook(true);
    load_playbook();
  };
  const close_playbook = () => {
    set_show_playbook(false);
    set_playbook_draft(null);
    set_playbook_status('none');
  };
  const select_playbook_option = (text) => {
    add_playbook_to_textbox(text);
    close_playbook();
  };

  // Full session-history pages in the hridaya CMS for this student.
  const all_sessions_url = (which) =>
    `/usersessions/${which}?id=${student_uuid}&user_type=STUDENT&name=${encodeURIComponent(
      student_name || ''
    )}`;

  // Open the session-recording modal for a given session uuid (the latest-1on1
  // button and each session card in Recent communications call this). Re-fetches
  // the replay only when a different session is opened.
  const open_session_video = (sessionUuid) => {
    if (!sessionUuid) return;
    set_show_session_video(true);
    if (sessionUuid === session_video_uuid && (replay || loading_replay)) return;
    set_session_video_uuid(sessionUuid);
    set_replay(null);
    set_loading_replay(true);
    const url = get_api_url(
      `/v2/sales_task_list/session_replay/${encodeURIComponent(sessionUuid)}`
    );
    get_api(url, true)
      .then((res) => {
        set_replay(res?.data?.data || {});
        set_loading_replay(false);
      })
      .catch(() => {
        set_replay({});
        set_loading_replay(false);
      });
  };

  if (loading) return <CustomLoading />;
  if (error) {
    return (
      <div className="text-sm" style={{ color: 'var(--state-error-text, #b91c1c)' }}>
        {error}
      </div>
    );
  }
  if (!form) return null;

  const tz = student?.iana_timezone;
  // moment-timezone's `z` token gives the short abbreviation (PST, IST, …).
  const current_time = tz
    ? moment(now).tz(tz).format('ddd, MMM D · h:mm:ss A z')
    : moment(now).format('ddd, MMM D · h:mm:ss A');
  const location = [student?.city, student?.state, student?.country].filter(Boolean).join(', ');
  const gender_age = [student?.gender, student?.age ? `${student.age} yrs` : '']
    .filter(Boolean)
    .join(', ');

  return (
    <div>
      {/* Student name → hridaya profile */}
      <div className="text-xl font-semibold mb-1">
        {student_uuid ? (
          <Link
            to={`/studentprofile?id=${encodeURIComponent(student_uuid)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline capitalize"
          >
            {student_name}
          </Link>
        ) : (
          <span className="capitalize" style={{ color: 'var(--text-primary)' }}>
            {student_name}
          </span>
        )}
      </div>

      {/* Rule config that generated this task:
          [Rule name] - [Day from last transition activity] - [generated date],
          e.g. "No book - Day 1 - 17 july 2026" (see rule_line.js). Replaces the
          old rule-name-only chip. */}
      <div className="mb-4 text-sm" style={{ color: 'var(--text-muted)' }}>
        {rule_config_line(task_label, form?.rule_day, form?.created_date_iso, form?.task_type)}
      </div>

      {/* Member details (from the student table) */}
      {student ? (
        <div className="py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
          <SectionHeader label="Member details" is_auto />
          <div
            className="rounded-md border px-4 py-3"
            style={{ borderColor: 'var(--border-default)', maxWidth: 720 }}
          >
            <div className="grid gap-x-6 gap-y-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
              {gender_age ? <AttrCell label="Gender, Age">{gender_age}</AttrCell> : null}
              <AttrCell label="Location">{location || '—'}</AttrCell>
              {tz ? <AttrCell label="Timezone">{tz}</AttrCell> : null}
              <AttrCell label="Current time (member)">
                <span className="font-medium">{current_time}</span>
              </AttrCell>
            </div>
            <div className="mt-3 flex flex-col gap-3">
              <AttrCell label="Goals">{student.goals || '—'}</AttrCell>
              <AttrCell label="Health history">{student.health_history || '—'}</AttrCell>
            </div>
          </div>
        </div>
      ) : null}

      {/* Latest 1on1 session done — button opening the recording popup */}
      <div className="py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
        <SectionHeader label="Latest 1on1 session done" is_auto />
        {last_1on1_session ? (
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => open_session_video(last_1on1_session.session_uuid)}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm hover:shadow-md hover:brightness-110 active:translate-y-px transition-all"
              style={{
                background: 'var(--brand-primary, #2563eb)',
                color: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)',
                cursor: 'pointer'
              }}
            >
              <FaExternalLinkAlt size={11} />
              View recording
            </button>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {last_1on1_session.start_time}
              {last_1on1_session.teacher_name ? ` · ${last_1on1_session.teacher_name}` : ''}
            </span>
          </div>
        ) : (
          <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
            No 1-on-1 sessions done yet.
          </div>
        )}
      </div>

      {/* AI: Getting to know student better — inline, quoted text */}
      <div className="py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
        <SectionHeader label="AI: Getting to know student better" is_auto />
        {ai_getting_to_know && ai_getting_to_know.length > 0 ? (
          <div
            className="rounded-md border px-4 py-3"
            style={{
              borderColor: 'var(--border-default)',
              background: 'var(--surface-sunken, #f8fafc)',
              maxWidth: 720
            }}
          >
            {ai_getting_to_know.map((cat) => (
              <div key={cat.key} className="mb-3 last:mb-0">
                <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>
                  {cat.label}
                </div>
                <div className="pl-3" style={{ borderLeft: '3px solid var(--border-default)' }}>
                  {cat.notes.map((note) => (
                    <p
                      key={note}
                      className="text-sm italic my-0.5"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      “{note}”
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
            No AI summary available for this member yet.
          </div>
        )}
      </div>

      {/* Membership timeline — ERC section, extended with trial bookings/joins,
          trial resets/ends and membership cancellations. */}
      <div className="py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
        <SectionHeader label="Membership timeline" is_auto />
        <MembershipTimeline
          events={timeline_events}
          next_renewal={auto.next_renewal_date || ''}
          sessions_remaining={
            typeof auto.sessions_remaining === 'number' ? auto.sessions_remaining : undefined
          }
        />
      </div>

      {/* Sessions — last 5 / next 5 cards, same as the ERC form. */}
      <div className="py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
        <SectionHeader label="Sessions" is_auto />
        <div className="flex gap-4 flex-wrap">
          <SessionList
            title="Last 5 sessions"
            list={sessions_overview.past}
            view_all_url={all_sessions_url('past')}
            view_all_label="View all past sessions"
          />
          <SessionList
            title="Next 5 sessions"
            list={sessions_overview.future}
            view_all_url={all_sessions_url('future')}
            view_all_label="View all future sessions"
          />
        </div>
      </div>

      {/* Recent communications — merged chat / SMS / WhatsApp / call / session
          timeline, newest first. */}
      <div className="py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
        <SectionHeader label="Recent communications" is_auto />
        <RecentCommunications
          events={recent_communications}
          onOpenTranscript={open_call_transcript}
          fetchRecording={fetch_call_recording}
          onPlaySession={open_session_video}
          onError={(m) => notify('error', m)}
        />
      </div>

      {/* Teacher Created 3 Month Plan — button opens the plan in a popup
          (instead of the ERC form's new-tab link). */}
      <div className="py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
        <SectionHeader label="Teacher Created 3 Month Plan" is_auto />
        {has_3m_plan ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => set_show_3m_plan(true)}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm hover:shadow-md hover:brightness-110 active:translate-y-px transition-all"
              style={{
                background: 'var(--brand-primary, #2563eb)',
                color: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)',
                cursor: 'pointer'
              }}
            >
              <FaExternalLinkAlt size={11} />
              View 3 Month Plan
            </button>
            <button
              type="button"
              onClick={download_plan_pdf}
              disabled={downloading_plan_pdf}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm hover:shadow-md hover:brightness-110 active:translate-y-px transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background: 'var(--surface-card)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-default)',
                cursor: downloading_plan_pdf ? 'not-allowed' : 'pointer'
              }}
            >
              <FaFilePdf size={11} />
              {downloading_plan_pdf ? 'Preparing PDF…' : 'Download PDF'}
            </button>
            {student_uuid ? (
              <button
                type="button"
                onClick={share_plan_url}
                disabled={sharing_plan}
                title="Share plan PDF link"
                aria-label="Share plan PDF link"
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm hover:shadow-md hover:brightness-110 active:translate-y-px transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--surface-card)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-default)',
                  cursor: sharing_plan ? 'not-allowed' : 'pointer'
                }}
              >
                <FaShareAlt size={11} />
                {sharing_plan ? 'Preparing link…' : 'Share'}
              </button>
            ) : null}
          </div>
        ) : (
          <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
            No 3-month plan.
          </div>
        )}
      </div>

      {/* Message teacher — same picker + composer + send/open-chat as the ERC form. */}
      <div className="py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
        <SectionHeader label="Message teacher" />
        <div className="flex flex-col gap-2" style={{ maxWidth: 560 }}>
          <div style={{ width: 300 }}>
            <SearchableSelect
              value={msg_teacher_uuid}
              options={message_teachers}
              placeholder="Select teacher..."
              clearable
              onChange={(val) => set_msg_teacher_uuid(val || '')}
            />
          </div>
          <textarea
            rows={8}
            value={teacher_message_value}
            onChange={(e) => set_msg_teacher_text(e.target.value)}
            className="w-full rounded-md border p-2 text-sm"
            style={{
              minHeight: 160,
              borderColor: 'var(--input-border)',
              background: 'var(--input-bg)',
              color: 'var(--input-text)'
            }}
          />
          <div className="flex gap-2">
            <Button disabled={sending_teacher || !msg_teacher_uuid} onClick={send_teacher_message}>
              {sending_teacher ? 'Sending…' : 'Send chat'}
            </Button>
            <Button type="secondary" disabled={!msg_teacher_uuid} onClick={open_teacher_chat}>
              Open chat
            </Button>
          </div>
        </div>
      </div>

      {/* Message student — chat message box + independent action buttons. The
          message playbook lives in its own popup (Other actions → Open message
          playbook). */}
      <div className="py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
        <SectionHeader label="Message student" />
        {/* Full section width (the old 560px cap belonged to the removed
            two-column layout with the inline playbook). Only the textarea stays
            capped for readability; the Other actions row can use the whole row
            so the buttons fit on one line where space allows. */}
        <div className="w-full">
          {/* Chat message box. This box sends over chat ONLY — email, call
              and SMS/WhatsApp are independent actions in the row below. */}
          <div className="flex flex-col gap-2">
            <textarea
              rows={7}
              value={msg_student_text}
              placeholder="Chat message to student…"
              onChange={(e) => set_msg_student_text(e.target.value)}
              className="w-full rounded-md border p-2 text-sm"
              style={{
                minHeight: 168,
                maxWidth: 560,
                borderColor: 'var(--input-border)',
                background: 'var(--input-bg)',
                color: 'var(--input-text)'
              }}
            />
            {/* The typed message box only sends over chat — keep that action
                grouped with the textarea. */}
            <div className="flex gap-2 flex-wrap">
              <Button
                disabled={sending_student || !student_uuid}
                onClick={send_student_message}
                custom_style={COMPACT_BTN}
              >
                {sending_student ? 'Sending…' : 'Send chat'}
              </Button>
            </div>

            {/* Independent actions — each opens a separate surface (call / email
                composer / chat / SMS-WhatsApp) and is NOT related to the message
                box above. Separated by a divider + label; the external-link arrow
                signals "opens elsewhere". */}
            <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-default)' }}>
              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>
                Other actions
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  disabled={call_active || !student_uuid}
                  onClick={call_student}
                  custom_style={{
                    ...COMPACT_BTN,
                    background: '#16a34a',
                    border: '1px solid #16a34a',
                    color: '#ffffff'
                  }}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <FaPhone size={11} style={{ transform: 'rotate(90deg)' }} />
                    {call_active ? 'On call…' : 'Call'}
                    <FaExternalLinkAlt size={10} />
                  </span>
                </Button>
                <Button
                  disabled={!student_uuid}
                  onClick={open_email_composer}
                  custom_style={COMPACT_BTN}
                >
                  <span className="inline-flex items-center gap-1.5">
                    Send email
                    <FaExternalLinkAlt size={10} />
                  </span>
                </Button>
                <Button
                  type="secondary"
                  disabled={!student_uuid}
                  onClick={open_student_chat}
                  custom_style={COMPACT_BTN}
                >
                  <span className="inline-flex items-center gap-1.5">
                    Open Chat
                    <FaExternalLinkAlt size={10} />
                  </span>
                </Button>
                <Button type="secondary" onClick={open_playbook} custom_style={COMPACT_BTN}>
                  <span className="inline-flex items-center gap-1.5">
                    Open message playbook
                    <FaExternalLinkAlt size={10} />
                  </span>
                </Button>
                {/* Opens the same SMS/WhatsApp conversation window as the student
                    profile page. Gated on the student's preference: only one of
                    Open SMS / Open WhatsApp shows (none when dispatch is 'none'
                    or the student has no phone). */}
                {can_sms ? (
                  <Button
                    type="secondary"
                    onClick={() => set_show_sms_window(true)}
                    custom_style={COMPACT_BTN}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {dispatch_method === 'whatsapp' ? 'Open WhatsApp' : 'Open SMS'}
                      <FaExternalLinkAlt size={10} />
                    </span>
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {show_sms_window && student_uuid ? (
        <SmsWindow
          student_uuid={student_uuid}
          student_name={student_name}
          close_fn={() => set_show_sms_window(false)}
          message_dispatch_method={dispatch_method}
          is_allowed_sms={dispatch_method === 'sms'}
          is_allowed_whatsapp={dispatch_method === 'whatsapp'}
          enable_reminder
          reminder_assignee_uuid={form?.assigned_to_ops_uuid}
          student_iana_timezone={form?.student_iana_timezone}
        />
      ) : null}

      {/* Rich email composer popup (shared with the Support Inbox). Opened from
          the "Send email" button; prefilled with the student as recipient. */}
      {show_email_composer ? (
        <Suspense fallback={null}>
          <NewConversationModal
            onClose={() => set_show_email_composer(false)}
            onCreated={() => {
              // The support create-conversation endpoint returns 200 even when the
              // outbound send failed (sent:false); in that case the saga already
              // toasts the failure — only claim success + tag the note when the
              // email actually went out.
              if (create_conversation_result?.sent !== false) {
                notify('success', 'Email sent to student');
                append_note_tag('#email');
              }
            }}
            agents={composer_agents}
            teams={composer_teams}
            inboxes={composer_inboxes}
            refresh={null}
            dispatch={dispatch}
            initialContactType="student"
            initialToEmail={student_email}
            initialContactName={student_name || ''}
          />
        </Suspense>
      ) : null}

      {/* Message playbook popup — the sales assistant generates a draft from
          this task's server-side context. Insert/copy act on the chat text box
          behind the popup. */}
      <CustomModal
        show={show_playbook}
        close={close_playbook}
        inner_style={{ width: 'min(720px, 94vw)', borderRadius: 12 }}
      >
        {show_playbook ? (
          <div>
            <div
              className="inline-flex items-center gap-1.5 text-sm font-semibold mb-3 pb-2 border-b w-full"
              style={{ color: 'var(--text-primary)', borderColor: 'var(--border-default)' }}
            >
              <FaBook size={12} style={{ color: '#7c3aed' }} />
              Message playbook
            </div>
            {playbook_status === 'loading' ? (
              <div className="py-12 flex flex-col items-center gap-3" aria-live="polite">
                <CustomLoading />
                <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  Processing, waiting for Antaryami…
                </div>
              </div>
            ) : null}
            {playbook_status === 'fail' ? (
              <div
                className="text-sm py-8 text-center"
                style={{ color: 'var(--state-error-text, #b91c1c)' }}
              >
                Could not generate the message.{' '}
                <button
                  type="button"
                  onClick={load_playbook}
                  className="underline"
                  style={{ color: 'inherit', background: 'none', border: 'none', padding: 0 }}
                >
                  Retry
                </button>
              </div>
            ) : null}
            {playbook_status === 'success' && playbook_draft?.no_message ? (
              <div className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }}>
                No message is recommended for this student right now.
              </div>
            ) : null}
            {playbook_status === 'success' && !playbook_draft?.no_message ? (
              <div>
                {Array.isArray(playbook_draft?.options) && playbook_draft.options.length === 2 ? (
                  <div>
                    <h2
                      className="text-lg font-semibold mb-4"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      Recommended messages
                    </h2>
                    <div className="flex flex-col gap-4">
                      {playbook_draft.options.map((option, index) => (
                        <div
                          key={index === 0 ? 'option-1' : 'option-2'}
                          className="rounded-lg border px-5 py-4"
                          style={{ borderColor: 'var(--border-default)' }}
                        >
                          <div
                            className="text-xs font-semibold uppercase tracking-wide mb-3"
                            style={{ color: 'var(--text-muted)' }}
                          >
                            Option {index + 1}
                          </div>
                          <div
                            className="whitespace-pre-wrap text-sm mb-4"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {option}
                          </div>
                          <Button
                            onClick={() => select_playbook_option(option)}
                            custom_style={COMPACT_BTN}
                          >
                            {index === 0 ? 'Select this' : 'Select that'}
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-center mt-5">
                      <Button type="secondary" onClick={close_playbook} custom_style={COMPACT_BTN}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }}>
                    Two message recommendations were not returned for this student.
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </CustomModal>

      {/* Review Notes — rich-text notes + attachments, saved to the student's
          notes thread on task completion (same as the early-retention form). */}
      <div className="py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
        <SectionHeader label="Review Notes" />
        <div className="w-full" style={{ maxWidth: 560 }}>
          {/* Attachments list + upload */}
          <div className="mb-2">
            {attachments.length ? (
              <div className="flex flex-col gap-1 mb-2">
                {attachments.map((att, idx) => (
                  <div key={att.key || idx} className="flex items-center gap-2 text-sm">
                    <FaPaperclip size={11} style={{ color: 'var(--text-muted)' }} />
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {att.name || att.key}
                    </a>
                    {!terminal ? (
                      <button
                        type="button"
                        aria-label="Remove attachment"
                        onClick={() => remove_attachment(idx)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 22,
                          width: 22,
                          borderRadius: 9999,
                          border: '1px solid var(--border-default)',
                          background: 'var(--surface-card)',
                          cursor: 'pointer',
                          padding: 0,
                          margin: 0
                        }}
                      >
                        <FaTimes size={10} color="#6b7280" />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
            {!terminal ? (
              <>
                <input
                  ref={file_input_ref}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handle_attachment_change}
                />
                <button
                  type="button"
                  onClick={() => file_input_ref.current && file_input_ref.current.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium border"
                  style={{
                    borderColor: 'var(--input-border)',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)',
                    cursor: uploading ? 'wait' : 'pointer',
                    margin: 0
                  }}
                >
                  <FaPaperclip size={11} />
                  {uploading ? 'Uploading…' : 'Add attachment'}
                </button>
              </>
            ) : null}
          </div>
          {terminal ? (
            <div
              className="text-sm"
              style={{ color: 'var(--text-primary)' }}
              dangerouslySetInnerHTML={{ __html: review_notes }}
            />
          ) : (
            <div className="w-full">
              <ReactQuill
                theme="snow"
                value={review_notes}
                onChange={set_review_notes}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Add notes…"
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer: task actions + progress counter. Sticky so it stays reachable
          while scrolling a long form. */}
      <div
        className="flex items-center gap-2 flex-wrap py-3 mt-6"
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--surface-card)',
          borderTop: '1px solid var(--border-default)'
        }}
      >
        <button
          type="button"
          onClick={done_and_next}
          disabled={acting || terminal}
          // Inline background/color beat the CMS's global orange `button` rules.
          className="px-4 py-2 rounded-md text-sm font-medium hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          style={{
            margin: 0,
            background: '#16a34a',
            border: '1px solid #16a34a',
            color: '#ffffff'
          }}
        >
          Done and Next
        </button>
        <button
          type="button"
          onClick={skip_and_next}
          disabled={acting}
          className="px-4 py-2 rounded-md text-sm font-medium border border-line bg-surface-card text-content-secondary hover:border-line-emphasis disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          style={{ margin: 0, background: 'var(--surface-card)', color: 'var(--text-primary)' }}
        >
          Skip and Next
        </button>
        <button
          type="button"
          onClick={go_to_list}
          disabled={acting}
          className="px-4 py-2 rounded-md text-sm font-medium border border-line bg-surface-card text-content-secondary hover:border-line-emphasis disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          style={{ margin: 0, background: 'var(--surface-card)', color: 'var(--text-muted)' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={done_and_back}
          disabled={acting || terminal}
          // Inline background/color beat the CMS's global orange `button` rules.
          className="px-4 py-2 rounded-md text-sm font-medium hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          style={{
            margin: 0,
            background: '#2563eb',
            border: '1px solid #2563eb',
            color: '#ffffff'
          }}
        >
          Done
        </button>
        {submitted ? (
          <span className="text-xs font-medium" style={{ color: 'var(--state-success, #16a34a)' }}>
            This task is already completed.
          </span>
        ) : null}
        {auto_closed ? (
          <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
            This task was closed automatically — the student is now a member.
          </span>
        ) : null}
        {stats ? (
          <span className="ml-auto text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
            <span style={{ color: 'var(--text-primary)' }}>{stats.remaining}</span> remaining ·{' '}
            <span style={{ color: 'var(--state-success, #16a34a)' }}>{stats.closed_today}</span>{' '}
            closed today
          </span>
        ) : null}
      </div>

      {/* Call transcript popup (from "Recent communications"). */}
      <CustomModal
        show={!!call_transcript}
        close={() => set_call_transcript(null)}
        inner_style={{ width: 'min(720px, 96vw)', borderRadius: 12 }}
      >
        {call_transcript ? (
          <div>
            <div
              className="text-sm font-semibold mb-2 pb-2 border-b"
              style={{ color: 'var(--text-primary)', borderColor: 'var(--border-default)' }}
            >
              Call transcript
              <span className="ml-2 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>
                {call_transcript.date}
              </span>
            </div>
            <div
              className="text-sm"
              style={{
                color: 'var(--text-primary)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '70vh',
                overflowY: 'auto'
              }}
            >
              {call_transcript.transcript || 'No transcript available.'}
            </div>
          </div>
        ) : null}
      </CustomModal>

      {/* 3-month-plan popup: the personalised-plan page in an iframe (it reads
          student_uuid from its own URL, so embedding keeps it untouched). */}
      <CustomModal
        show={show_3m_plan}
        close={() => set_show_3m_plan(false)}
        inner_style={{ width: '92%', maxWidth: 1200, borderRadius: 12 }}
      >
        {show_3m_plan && student_uuid ? (
          <iframe
            title="3 Month Plan"
            src={`/personalised-plan?student_uuid=${encodeURIComponent(student_uuid)}`}
            style={{
              width: '100%',
              height: '78vh',
              border: '1px solid var(--border-default)',
              borderRadius: 8,
              background: '#ffffff'
            }}
          />
        ) : null}
      </CustomModal>

      {/* Session-recording popup: player (left) with chapter strip below,
          transcript (right) — same layout as the early-retention form. Driven
          by the latest-1on1 button and each session card in Recent
          communications (keyed by session_video_uuid). */}
      <CustomModal
        show={show_session_video}
        close={() => set_show_session_video(false)}
        inner_style={{ width: '90%', maxWidth: 1100, borderRadius: 12 }}
      >
        {show_session_video && session_video_uuid ? (
          <div>
            <div className="flex items-center mb-3">
              <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Session recording
              </div>
              <a
                href={`/sessiondetails?id=${encodeURIComponent(session_video_uuid)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-3 rounded-md border px-2.5 py-0.5 text-xs font-semibold text-blue-600 hover:underline"
                style={{
                  borderColor: 'var(--border-default)',
                  background: 'var(--surface-sunken, #f1f5f9)'
                }}
              >
                Session details ↗
              </a>
            </div>
            <div className="flex gap-3 items-stretch" style={{ height: '62vh' }}>
              <div className="flex flex-col" style={{ width: '70%', overflow: 'hidden' }}>
                {loading_replay ? (
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Loading recording…
                  </div>
                ) : null}
                {!loading_replay && replay?.replay_url ? (
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <CustomPlayer
                      fill
                      video_url={replay.replay_url}
                      player_ref={session_player_ref}
                    />
                  </div>
                ) : null}
                {!loading_replay && !replay?.replay_url ? (
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    No recording available for this session.
                  </div>
                ) : null}
                {!loading_replay && replay?.replay_url && (replay.chapters || []).length > 0 ? (
                  <div
                    className="flex gap-2 mt-2 pb-1"
                    style={{ overflowX: 'auto', flexShrink: 0 }}
                  >
                    {replay.chapters.map((ch) => (
                      <button
                        key={`${ch.start_seconds}-${ch.title}`}
                        type="button"
                        onClick={() => seek_session_video(ch.start_seconds)}
                        title={ch.summary || ch.title}
                        className="flex-shrink-0 rounded-md border p-1 text-xs text-left"
                        style={{
                          borderColor: 'var(--border-default)',
                          background: 'var(--surface-card)',
                          cursor: 'pointer',
                          width: 150
                        }}
                      >
                        {ch.thumb ? (
                          <img
                            src={ch.thumb}
                            alt={ch.title}
                            className="rounded w-full mb-1"
                            style={{ height: 80, objectFit: 'cover' }}
                          />
                        ) : null}
                        <div className="px-1 pb-0.5">
                          <span className="font-semibold text-blue-600">{ch.start_label}</span>
                          <span className="ml-1.5" style={{ color: 'var(--text-primary)' }}>
                            {ch.title}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <div
                className="rounded-md border p-2"
                style={{
                  width: '30%',
                  overflowY: 'auto',
                  borderColor: 'var(--border-default)',
                  background: 'var(--surface-sunken, #f1f5f9)'
                }}
              >
                <div
                  className="text-xs font-semibold mb-1.5"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Transcript
                </div>
                <div
                  className="text-xs"
                  style={{
                    color: 'var(--text-primary)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {loading_replay ? 'Loading…' : replay?.transcript || 'No transcript available.'}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </CustomModal>

      {/* Floating "+ Reminder" button — opens the /reminders create form seeded
          from this sales task (same as the early-retention form). */}
      {student_uuid ? (
        <button
          type="button"
          onClick={() => set_show_reminder(true)}
          title="Create a reminder from this task"
          style={{
            position: 'fixed',
            bottom: 28,
            right: 28,
            zIndex: 1000,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '11px 22px 11px 13px',
            borderRadius: 9999,
            background: 'var(--brand-primary)',
            color: 'var(--text-inverse)',
            border: 'none',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25)'
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 26,
              height: 26,
              borderRadius: 9999,
              background: '#ffffff',
              flexShrink: 0
            }}
          >
            <FaPlus size={13} style={{ color: 'var(--brand-primary)' }} />
          </span>{' '}
          Reminder
        </button>
      ) : null}

      <CustomModal
        show={show_reminder}
        close={() => set_show_reminder(false)}
        title="New reminder"
        inner_style={{ width: 'min(640px, 96vw)', maxWidth: 'none' }}
      >
        {show_reminder && student_uuid ? (
          <ReminderForm
            key={`stl-reminder-${uuid}`}
            initial={reminder_initial}
            lockedLinkedUser={reminder_locked_user}
            selfUuid={me?.uuid}
            onSubmit={handle_reminder_submit}
            onCancel={() => set_show_reminder(false)}
            submitLabel="Create"
          />
        ) : null}
      </CustomModal>
    </div>
  );
}

SalesTaskForm.propTypes = {
  uuid: PropTypes.string.isRequired
};

export default SalesTaskForm;
